import React from 'react';

import { Carousel } from 'antd/es';
import { Form, Formik } from 'formik';

import { useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import SearchFieldAuto from '@app/components/UI/Forms/SearchField/SearchFieldAuto';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import MiddleText from '@app/components/UI/MiddleText/MiddleText';
import SimpleMap from '@app/components/UI/SimpleMap/SimpleMap';
import { ListOption } from '@app/objects/SidebarProps';
import { StatesListItem } from '@app/objects/StatesListItem';
import { Veterinarian } from '@app/objects/Veterinarian';
import '@app/scss/components/findVet.scss';
import '@app/scss/components/vetsMap.scss';
import '@app/scss/components/sliderVet.scss';

const arrowIcon = require('@app/images/arrow_circle.svg');
const arrowIconHovered = require('@app/images/button_slider.svg');

interface VetsMapProps {
	stateName: string;
	setStateName: (arg: string) => void;
}

const VetsMap: React.FC<VetsMapProps> = ({
	stateName,
	setStateName,
}) => {
	const slider = React.useRef<any>();
	const [stateList, setStateList] = React.useState<Array<StatesListItem>>([]);
	const [activeStateItem, setActiveStateItem] = React.useState<string>();
	const stateListOptions: Array<ListOption> = [];

	const context = useItemsProviderContext<Veterinarian>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items, loading },
		actions: { handleChange },
	} = context;

	const transformStateList = () => {
		stateList && (
			stateList.map((item, index) => {
				stateListOptions.push({ value: item.name });
			})
		);
	};

	React.useEffect(() => {
		handleChange({
			count: 30,
		});
	}, [stateName]);

	React.useEffect(() => {
		rest.get<List<StatesListItem>>('v1/states').then((res) => {
			setStateList(res.list);
		});
	}, []);

	transformStateList();
	return (
		<div className="vets-map">
			<div className="vets-map-wrapper">
				<div className="vets-map-body">
					<h2>
						A passionate network of
						{' '}
						<span className="highlight">nationwide</span>
						{' '}
						veterinarians
					</h2>

					<Formik
						onSubmit={(values) => {
							console.log(values);
						}}
						initialValues={{ state: '' }}
					>
						{(formikBag) => {
							return (
								<>
									<Form className="vets-map__state-form" autoComplete="off">
										<SearchFieldAuto
											name="state"
											placeholder="Select a state"
											listOptions={stateListOptions}
											formikBag={formikBag}
											onSelect={(value, option) => {
												formikBag.setFieldValue('state', value);
												setStateName(value);
												setActiveStateItem(value);
											}}
											onChange={(value) => {
												formikBag.setFieldValue('state', value);
												!value && setStateName('');
												setActiveStateItem('');
											}}
											searchButton={false}
											allowClear
										/>
									</Form>

									<div className="vets-map-body__container">
										<SimpleMap<StatesListItem>
											onClick={(currentState) => {
												setActiveStateItem(currentState.name);
												setStateName && setStateName(currentState.name);
												formikBag.setFieldValue('state', currentState.name);
											}}
											states={stateList}
											getFillColor={(state) => (
												activeStateItem === state.name
													? '#9c8cae'
													: state.veterinarians > 0
														? '#bdb0cb'
														: '#d5cce0'
											)}
											getTooltip={(state) => `${state.veterinarians} local vets`}
										/>
									</div>
									<div className="vets-map-body__tag">
										<img src={require('@app/images/pin_circle.svg')} alt="pin" />
										<span>{`Viewing ${stateName.length ? stateName : 'all'} Vets`}</span>
									</div>
								</>
							);
						}}
					</Formik>

					<div className="vets-map-carousel">
						{items.length > 4 && (
							<div className="slider-buttons">
								<div className="slider-button_left__wrapper">
									<img
										className="slider-button slider-button_left"
										src={arrowIcon}
										alt="Prev"
										onClick={() => slider.current!.prev()}
									/>
									<img
										className="slider-button slider-button_left_hovered"
										src={arrowIconHovered}
										alt="Prev"
										onClick={() => slider.current!.prev()}
									/>
								</div>
							</div>
						)}

						{loading ? (
							<BounceLoader />
						) : items.length ? (
							<Carousel
								ref={slider}
								initialSlide={0}
								slidesToShow={items.length >= 5 ? 5 : items.length}
								dots={false}
								infinite
								variableWidth
								swipeToSlide
								swipe
								responsive={[
									{
										breakpoint: 1530,
										settings: {
											slidesToShow: items.length >= 4 ? 4 : items.length,
										},
									},
									{
										breakpoint: 1150,
										settings: {
											slidesToShow: items.length >= 3 ? 3 : items.length,
										},
									},
									{
										breakpoint: 830,
										settings: {
											slidesToShow: items.length >= 2 ? 2 : items.length,
										},
									},
									{
										breakpoint: 768,
										settings: {
											slidesToShow: 1,
										},
									},
								]}
							>
								{items.map((item, index) => {
									return (
										<div
											key={item.id}
											className="vets-map-carousel__item"
											onClick={() => slider?.current.goTo(index)}
										>
											<div className="vets-map-carousel__item-avatar">
												<CustomAvatar
													src={item.avatar}
													alt="veterinarian"
													className="slider-vet-item"
													maxWidth={120}
													maxHeight={120}
												/>
											</div>
											<div className="vets-map-carousel__item-descr">
												<h3>{`Dr. ${item.firstName}`}</h3>
												<p>{item.areaName}</p>
											</div>
										</div>
									);
								})}
							</Carousel>
						) : (
							<MiddleText
								title="As it turns out..."
								// eslint-disable-next-line max-len
								text="You're in one of the few areas where we don't have a Lap of Love veterinarian (not yet at least). One day we hope to have a doctor available to every family in the United States!"
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default VetsMap;
